/**
 * <promo-tab>
 *
 * A flame/chrome floating "Get 5% Off" tab that links to Klaviyo email signup. Three modes:
 *   - 'klaviyo_form': clicking opens an existing Klaviyo form by ID (window._klOnsite openForm).
 *   - 'panel': opens a built-in slide-up panel; the email submits to a Klaviyo list.
 *   - 'link': the tab is a normal link (handled by the <a>).
 *
 * The panel submits to Klaviyo's list-subscribe endpoint (no private key needed). Shoppers
 * enter their own email — nothing is submitted automatically.
 */
class PromoTab extends HTMLElement {
  #panel = null;
  #action = 'panel';
  #timer = 0;
  #onScroll = null;
  #onExit = null;

  connectedCallback() {
    this.#panel = this.querySelector('[data-pt-panel]');
    this.#action = this.dataset.action || 'panel';

    this.querySelector('[data-pt-tab]')?.addEventListener('click', this.#onTab);
    this.querySelector('[data-pt-close]')?.addEventListener('click', () => this.#close());
    this.querySelector('[data-pt-form]')?.addEventListener('submit', this.#submit);
    this.#panel?.addEventListener('click', (e) => {
      if (e.target === this.#panel) this.#close();
    });
    document.addEventListener('keydown', this.#onKey);

    this.#bindAuto();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKey);
    this.#teardownAuto();
  }

  /* ---- Auto-open as a popup (in addition to the always-visible sticky tab) ---- */
  #bindAuto() {
    const mode = this.dataset.auto || 'off';
    if (mode === 'off' || this.#autoSeen()) return;
    if (mode === 'delay') {
      this.#timer = window.setTimeout(() => this.#autoOpen(), (parseFloat(this.dataset.autoDelay || '8') || 8) * 1000);
    } else if (mode === 'exit') {
      this.#onExit = (e) => {
        if (e.clientY <= 0) this.#autoOpen();
      };
      document.addEventListener('mouseout', this.#onExit);
      this.#bindAutoScroll(0.6); // touch fallback (no exit-intent on mobile)
    } else {
      this.#bindAutoScroll((parseFloat(this.dataset.autoScroll || '45') || 45) / 100);
    }
  }

  #bindAutoScroll(threshold) {
    this.#onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= threshold) this.#autoOpen();
    };
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  #autoOpen() {
    this.#teardownAuto();
    this.#markAutoSeen();
    if (this.#action === 'klaviyo_form') {
      const id = this.dataset.formId;
      window._klOnsite = window._klOnsite || [];
      if (id) window._klOnsite.push(['openForm', id]);
      return;
    }
    if (this.#action === 'link') return;
    this.#open();
  }

  #autoKey() {
    return `pt-auto-${this.id || 'promo'}`;
  }
  #autoSeen() {
    const f = this.dataset.autoFrequency || 'session';
    if (f === 'always') return false;
    if (f === 'session') return sessionStorage.getItem(this.#autoKey()) != null;
    return localStorage.getItem(this.#autoKey()) === new Date().toDateString();
  }
  #markAutoSeen() {
    const f = this.dataset.autoFrequency || 'session';
    if (f === 'session') sessionStorage.setItem(this.#autoKey(), '1');
    else if (f === 'daily') localStorage.setItem(this.#autoKey(), new Date().toDateString());
  }
  #teardownAuto() {
    if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
    if (this.#onExit) document.removeEventListener('mouseout', this.#onExit);
    if (this.#timer) clearTimeout(this.#timer);
  }

  #onTab = (e) => {
    if (this.#action === 'klaviyo_form') {
      e.preventDefault();
      const id = this.dataset.formId;
      window._klOnsite = window._klOnsite || [];
      if (id) window._klOnsite.push(['openForm', id]);
      return;
    }
    if (this.#action === 'link') return; // the <a> handles navigation
    e.preventDefault();
    this.#open();
  };

  #open() {
    if (!this.#panel) return;
    this.#panel.hidden = false;
    void this.#panel.offsetWidth;
    this.#panel.setAttribute('data-open', '');
    this.#panel.querySelector('input[type="email"]')?.focus();
  }

  #close() {
    if (!this.#panel) return;
    this.#panel.removeAttribute('data-open');
    const panel = this.#panel;
    setTimeout(() => {
      panel.hidden = true;
    }, 260);
  }

  #onKey = (e) => {
    if (e.key === 'Escape' && this.#panel?.hasAttribute('data-open')) this.#close();
  };

  #submit = async (e) => {
    e.preventDefault();
    const input = this.querySelector('[data-pt-email]');
    const msg = this.querySelector('[data-pt-msg]');
    const btn = this.querySelector('[data-pt-submit]');
    const email = (input?.value || '').trim();
    if (!email || !email.includes('@')) {
      this.#msg(msg, 'Please enter a valid email.', false);
      return;
    }

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const company = this.dataset.publicKey; // Klaviyo public API key (company_id)
      const list = this.dataset.listId;

      // Real Klaviyo subscribe (current Client Subscriptions API — no private key needed).
      if (company && list) {
        const payload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: this.dataset.source || 'Showcase promo tab',
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: { list: { data: { type: 'list', id: list } } },
          },
        };
        const res = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
            body: JSON.stringify(payload),
          }
        );
        // 202 Accepted = queued successfully.
        if (!res.ok && res.status !== 202) {
          this.#msg(msg, 'Sorry, that didn’t go through — please try again.', false);
          return;
        }
      }

      this.#msg(msg, this.dataset.success || 'Check your email for your 5% code!', true);
      this.querySelector('[data-pt-form]')?.reset();
      setTimeout(() => this.#close(), 2400);
    } catch (err) {
      this.#msg(msg, 'Something went wrong. Please try again.', false);
    } finally {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  };

  #msg(el, text, ok) {
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
    el.classList.toggle('promo-tab__msg--ok', ok);
    el.classList.toggle('promo-tab__msg--err', !ok);
  }
}

if (!customElements.get('promo-tab')) {
  customElements.define('promo-tab', PromoTab);
}
